'use client';

import { Button, Tooltip } from '@common/components';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Transition } from 'react-transition-group';

import { UnauthQueryOptions } from '@/api';
import type { PolicyDetail } from '@/api/getPolicyDetail';
import { Image } from '@/components/client';

interface PolicyContentProps {
  data: PolicyDetail;
  className?: string;
}

export const PolicyContent = ({ data, className }: PolicyContentProps) => {
  const { data: dictionary = [] } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/policy-dictionary'](),
    select: ({ context }) => context?.policyDictionaryList ?? [],
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return (
    <ContentContainer className={className}>
      <ContentSection
        sectionTitle="누가 받을 수 있나요?"
        subSections={[{ subTitle: '지원대상', content: data.descTarget }]}
        dictionary={dictionary}
      />
      <ContentSection
        sectionTitle="얼마나 받을 수 있나요?"
        subSections={[{ subTitle: '지원내용', content: data.descSupport }]}
        dictionary={dictionary}
      />
      <ContentSection
        sectionTitle="어떻게 신청하나요?"
        subSections={[
          { subTitle: '신청기간', content: data.dueDateBetween },
          { subTitle: '신청방법', content: data.descApply },
        ]}
        dictionary={dictionary}
      />
      <ContentSection
        sectionTitle="무엇을 준비해야 하나요?"
        subSections={[{ subTitle: '관련정보', content: data.relatedInfo }]}
        dictionary={dictionary}
      />
      <PolicyAgency data={data} />
    </ContentContainer>
  );
};

interface PolicyDictionaryItem {
  keyword: string;
  description: string;
}

interface ContentContainerProps {
  className?: string;
  children: React.ReactNode;
}

const COLLAPSED_HEIGHT_PX = 300;
const GRADIENT_OVERLAY_PX = 96;

const ContentContainer = ({ children, className }: ContentContainerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const articleRef = useRef<HTMLElement>(null);
  const [height, setHeight] = useState(0);

  return (
    <Transition in={isOpen} nodeRef={articleRef} timeout={150}>
      {(state) => (
        <article
          ref={articleRef}
          className={cn(
            'relative transition-[height] animate-ease-in',
            { 'overflow-hidden': state !== 'entered' },
            className,
          )}
          style={{
            height: isOpen ? height + GRADIENT_OVERLAY_PX : COLLAPSED_HEIGHT_PX,
          }}
        >
          <div ref={ref} className="flex flex-col">
            {children}
          </div>

          <div
            className={cn(
              isOpen
                ? 'mt-40'
                : [
                    'absolute bottom-0 w-full pt-96',
                    'bg-gradient-to-t from-Gray-white to-transparent',
                  ],
            )}
          >
            <div className="w-full bg-Gray-white">
              <Button
                className="w-full"
                dimensions="H48"
                theme="sub-line"
                onClick={() => {
                  if (ref.current) {
                    setHeight(ref.current.clientHeight);
                  }
                  setIsOpen((prev) => !prev);
                }}
              >
                {isOpen ? '접기' : '상세 내용 더보기'}
              </Button>
            </div>
          </div>
        </article>
      )}
    </Transition>
  );
};

interface ContentSubSection {
  subTitle: string;
  content?: string;
}

interface ContentSectionProps {
  sectionTitle: string;
  subSections: ContentSubSection[];
  dictionary: PolicyDictionaryItem[];
  className?: string;
}

const ContentSection = ({
  sectionTitle,
  subSections,
  dictionary,
  className,
}: ContentSectionProps) => {
  const visibleSubSections = subSections.filter(({ content }) => !!content);

  if (visibleSubSections.length === 0) return null;

  return (
    <section className="flex w-full flex-col gap-20 py-36">
      <h2 className="w-full font-700 tracking-[-0.4px] text-Gray-900 typo-h7">
        {sectionTitle}
      </h2>
      <div className="flex w-full flex-col gap-40">
        {visibleSubSections.map(({ subTitle, content }) => (
          <div key={subTitle} className="flex w-full flex-col gap-8">
            <h3 className="whitespace-nowrap font-600 text-Gray-900 typo-body3">
              {subTitle}
            </h3>
            {content?.split('\n').map((paragraph, index) => {
              const text = paragraph.trimEnd();

              let parts = [{ text, description: '' }];

              dictionary.forEach(({ keyword, description }) => {
                parts = parts.flatMap((part) => {
                  if (part.description) return [part];

                  const splits = part.text.split(
                    new RegExp(`(${keyword})`, 'g'),
                  );

                  return splits.map((text) => ({
                    text,
                    description: text === keyword ? description : '',
                  }));
                });
              });

              return (
                <p
                  key={index}
                  className={cn(
                    'whitespace-pre-wrap break-keep font-400 text-Gray-800 typo-body3',
                    className,
                  )}
                >
                  {parts.map(({ text, description }, index) =>
                    description ? (
                      <Tooltip
                        key={index}
                        label={
                          <article className="font-400 typo-caption1">
                            <h5 className="mb-2 font-700 typo-caption1">
                              {text}
                            </h5>
                            {description}
                          </article>
                        }
                        triggerClassName="bg-G-100"
                      >
                        {text}
                      </Tooltip>
                    ) : (
                      text
                    ),
                  )}
                </p>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
};

const PolicyAgency = ({ data }: { data: PolicyDetail }) => {
  const { agency, agencyLogo, agencyTel, infoUrl } = data;

  if (!agency && !agencyTel && !infoUrl) return null;

  return (
    <section className="flex w-full flex-col gap-20 py-36">
      <h2 className="w-full font-700 tracking-[-0.4px] text-Gray-900 typo-h7">
        관할기관 정보
      </h2>
      <div className="flex w-full flex-col items-start rounded-large bg-Gray-25 px-20 py-16">
        <div className="flex w-full flex-col items-start gap-8">
          {agency ? (
            <div className="flex items-center gap-6">
              {agencyLogo ? (
                <Image
                  alt={agency}
                  className="rounded-small border border-Gray-300 bg-Gray-white object-contain p-2"
                  height={32}
                  src={agencyLogo}
                  width={32}
                />
              ) : null}
              <p className="whitespace-nowrap font-600 text-Gray-900 typo-body3">
                {agency}
              </p>
            </div>
          ) : null}
          {agencyTel || infoUrl ? (
            <dl className="flex w-full flex-col gap-4 text-Gray-800 typo-body3">
              {agencyTel ? (
                <div className="flex gap-8">
                  <dt className="w-56 shrink-0 font-600 text-Gray-600">전화</dt>
                  <dd className="min-w-0 flex-1">
                    <a className="underline" href={`tel:${agencyTel}`}>
                      {agencyTel}
                    </a>
                  </dd>
                </div>
              ) : null}
              {infoUrl ? (
                <div className="flex gap-8">
                  <dt className="w-56 shrink-0 font-600 text-Gray-600">
                    홈페이지
                  </dt>
                  <dd className="min-w-0 flex-1">
                    <a
                      className="break-all underline"
                      href={infoUrl}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {infoUrl}
                    </a>
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
        </div>
      </div>
    </section>
  );
};
